import { ContractorHeader } from '@/components/AppLayout/ContractorHeader'
import { ContractorSidebar } from '@/components/AppLayout/ContractorSidebar'
import { ClientRecorderProvider } from '@/components/ClientRecorder'
import { TermsAcceptanceGate } from '@/components/TermsAcceptanceGate'
import { useNotificationStream } from '@/hooks/notifications'
import { useLogout } from '@/hooks/useLogout'
import { errorCode, signOutReason } from '@/lib/authErrors'
import { getApi } from '@/stores/api'
import {
  destroySessionToken,
  getPermissions,
  getSessionToken,
  isSwitchingTenantAtom,
  setPermissions,
  setSessionUser,
  setTenant,
  setTenants,
  setTermsStatus,
  termsStatusAtom,
} from '@/stores/session'
import { SidebarInset, SidebarProvider } from '@repo/ui/components'
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { Loader2 } from 'lucide-react'
import { Toaster } from 'sonner'

export const Route = createFileRoute('/_contractor')({
  beforeLoad: async ({ location }) => {
    const emailParam = new URLSearchParams(location.searchStr).get('email') ?? undefined
    const loginSearch = { redirect: location.pathname, email: emailParam }

    const sessionToken = getSessionToken()
    if (!sessionToken) {
      throw redirect({ to: '/login', search: loginSearch })
    }

    // Verify session is valid
    try {
      const api = getApi()
      const res = await api['portal-auth'].contractor.session.verify.$post({
        json: { sessionToken },
      })

      if (!res.ok) {
        destroySessionToken()
        throw redirect({ to: '/login', search: loginSearch })
      }

      const data = await res.json()
      if (data.tenant) {
        setTenant(data.tenant)
      }
      if (data.tenants) {
        setTenants(data.tenants)
      }
      setSessionUser(data.user)
      setTermsStatus(data.terms)

      // beforeLoad runs on every navigation; the grants only need reading once
      // per page load.
      if (!getPermissions()) {
        const me = await api.users.me.$post()
        setPermissions((await me.json()).permissions)
      }
    } catch (e) {
      if (e instanceof Response || (e && typeof e === 'object' && 'to' in e)) throw e
      destroySessionToken()
      throw redirect({
        to: '/login',
        search: { ...loginSearch, reason: signOutReason(errorCode(e)) },
      })
    }
  },
  component: ContractorLayout,
})

function ContractorLayout() {
  const isSwitching = useAtomValue(isSwitchingTenantAtom)
  const termsStatus = useAtomValue(termsStatusAtom)
  const logout = useLogout()
  useNotificationStream()

  // Versioned terms-of-service gate (PET-733). Rendered in place of the app —
  // not a route — so it can't be navigated around.
  if (termsStatus?.currentVersion && !termsStatus.accepted) {
    return (
      <TermsAcceptanceGate
        currentVersion={termsStatus.currentVersion}
        onSignOut={() => void logout()}
      />
    )
  }

  return (
    <ClientRecorderProvider>
      <div className="text-sm text-foreground subpixel-antialiased">
        <SidebarProvider className="fixed top-0 left-0 h-svh w-screen overflow-hidden">
          <ContractorSidebar />
          <SidebarInset className="min-w-0 overflow-hidden">
            <ContractorHeader />
            <main className="relative h-full w-full flex-1 overflow-y-auto bg-background">
              <div className="mx-auto w-full max-w-7xl px-6 py-6">
                {isSwitching ? (
                  <div className="flex items-center justify-center py-24">
                    <Loader2 className="size-8 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <Outlet />
                )}
              </div>
            </main>
          </SidebarInset>
        </SidebarProvider>
        <Toaster position="top-right" richColors />
      </div>
    </ClientRecorderProvider>
  )
}
